import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  Drawer,
} from "@mui/material";
import { fetchGet, fetchPost } from "../util/helpers";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";

import {
  IconApps,
  IconBuilding,
  IconFileDescription,
  IconInfoCircle,
  IconLink,
  IconListCheck,
  IconMapPin,
  IconMicrophone2,
  IconPresentation,
  IconUserPlus,
  IconUsersGroup,
} from "@tabler/icons-react";
import UserContext from "../contexts/UserContext";
import CreatorWorkstreamTab from "./CreatorWorkstreamTab";
import CreatorAnalytics from "./CreatorAnalytics";
import CreatorLiveContent from "./CreatorLiveContent";

export default function CreatorCampaignPage() {
  const [campaign, setCampaign] = useState({});
  const { campaignId } = useParams();
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const [tabValue, setTabValue] = useState("details");
  const [matches, setMatches] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applicationData, setApplicationData] = useState({
    rate: "",
    concept: "",
    address: "",
    additionalInfo: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCampaign = async () => {
    if (campaignId) {
      console.log("id", campaignId);
      const response = await fetchGet(`/brand/campaigns/${campaignId}`);
      if (response.result) {
        setCampaign(response.result);
      }
    }
  };

  // Creators on our platform that have applied to the campaign
  const fetchApplications = async () => {
    // setIsLoading(true);
    if (campaignId) {
      const response = await fetchGet(
        `/brand/campaigns/${campaignId}/responses`
      );
      if (response.result) {
        const creatorTemp = [...response.result];
        creatorTemp.sort(
          (a, b) =>
            (b.stats ? b.stats.totalFollowers : 0) -
            (a.stats ? a.stats.totalFollowers : 0)
        );
        // if (
        //     creatorTemp.length > 0 &&
        //     creatorTemp[0].stats &&
        //     creatorTemp[0].stats.totalFollowers > 100000
        // ) {
        //     setBestMatchCreators(creatorTemp.slice(0, 6));
        // }
        setApplications(response.result);
      }
      // setIsLoading(false);
    }
  };


  // Creators that have been matched to the campaign. They accepted the invite or the brand accepted their application.
  // Creators must sign up for the platform to be matched.
  // TODO: store creator object in matches
  const fetchMatches = async () => {
    if (campaignId) {
      const response = await fetchGet(`/brand/campaigns/${campaignId}/matches`);
      if (response.result) {
        for (const creator of response.result) {
          try {
            const response = await fetchGet(
              `/brand/campaigns/${campaignId}/creator/${creator.userDocId}/analytics`
            );
            if (response.result) {
              response.result.sort((a, b) => new Date(a.date) - new Date(b.date));
              creator.stats = response.result;
            }
          } catch (error) {
            console.log("error", error);
          }
        }
        setMatches(response.result);
      }
    }
  };

  useEffect(() => {
    fetchCampaign();
    fetchMatches();
    fetchApplications();
  }, [campaignId, user]);

  const tabs = [
    // {
    //     name: "Overview",
    //     value: "overview",
    // },
    { name: "Details", value: "details" },
    { name: "Workstream", value: "workstream" },
    { name: "Analytics", value: "analytics" },
    { name: "Live Content", value: "live" },
  ];
  
  // Check if the current user is already matched with this campaign
  const isUserMatched = matches.some(match => match.userDocId === user.userDocId);
  
  // Check if the current user has already applied to this campaign
  const hasUserApplied = applications.some(app => app.userDocId === user.userDocId);

  const handleApplicationDataChange = (field, value) => {
    setApplicationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitApplication = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetchPost('/creator/responses', {
        campaignId: campaignId,
        status: 'interested',
        rate: applicationData.rate,
        concept: applicationData.concept,
        address: applicationData.address,
        additionalInfo: applicationData.additionalInfo
      });
      
      if (response.result === "Success") {
        toast.success("Application submitted successfully!");
        setApplyOpen(false);
        // Reset form data
        setApplicationData({
          rate: "",
          concept: "",
          address: "",
          additionalInfo: ""
        });
        // Refresh applications to show the new one
        fetchApplications();
      } else {
        toast.error("Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("An error occurred while submitting your application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Box sx={{ height: "100%", width: "100%" }}>
            <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
           <Drawer open={applyOpen} onClose={()=>{setApplyOpen(false)}} anchor="right">
            <Stack direction="column" spacing={2} sx={{padding: "3%"}}>
              <Box style={{borderRadius: "12px", background: "var(--color-gradient)", width: "100%", height: 80, textAlign: "center", alignItems: "center", justifyContent: "center", display: "flex"}}>
                <Typography variant="h5" color={"white"}>Apply for this campaign</Typography>
              </Box>
              <Typography variant="body2" color={"var(--main-text-color)"}>
                Partnership Rates: What is your proposed rate for this campaign? (USD)
              </Typography>
              <TextField 
                label="Rate" 
                value={applicationData.rate}
                onChange={(e) => handleApplicationDataChange('rate', e.target.value)}
              />
              <Typography variant="body2" color={"var(--main-text-color)"}>
                Campaign Concept (Optional)
              </Typography>
              <TextField 
                label="Concept" 
                multiline 
                rows={4} 
                value={applicationData.concept}
                onChange={(e) => handleApplicationDataChange('concept', e.target.value)}
              />
              <Typography variant="body2" color={"var(--main-text-color)"}>
                Shipping Address (Optional)
              </Typography>
              <TextField 
                label="Address" 
                multiline 
                rows={4} 
                value={applicationData.address}
                onChange={(e) => handleApplicationDataChange('address', e.target.value)}
              />
              <Typography variant="body2" color={"var(--main-text-color)"}>
                Additional Information: Anything else you'd like to share? (Optional)
              </Typography>
              <TextField 
                label="Additional Information" 
                multiline 
                rows={4} 
                value={applicationData.additionalInfo}
                onChange={(e) => handleApplicationDataChange('additionalInfo', e.target.value)}
              />
              <Button 
                variant="contained" 
                className="gradient-button" 
                fullWidth
                onClick={handleSubmitApplication}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Stack>
           </Drawer>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          height: "160px",
          backgroundColor: "white",
          paddingTop: "24px",
          paddingX: "20px",
        }}
      >
        <Stack direction="column" justifyContent="space-between" height="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2}>
              <img
                className="campaign-image"
                src={campaign.image}
                alt={campaign.name}
              />
              <Stack direction="column" spacing={1}>
                <Typography variant="h3" color={"var(--main-text-color)"}>
                  {campaign.name}
                </Typography>
               
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2}>
              {!isUserMatched && !hasUserApplied && (
                <Tooltip title={<Typography variant="body2">Apply to this campaign.</Typography>}>
                  <Button
                    onClick={()=>{setApplyOpen(true)}}
                  variant="contained"
                  className="gradient-button"
                  startIcon={<IconUserPlus className="icon" size="18" />}
                >
                    Apply
                  </Button>
                </Tooltip>
              )}
              {isUserMatched && (
                <Chip 
                  label="Matched" 
                  color="success" 
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {hasUserApplied && !isUserMatched && (
                <Chip 
                  label="Application Pending" 
                  color="warning" 
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          </Stack>
          <Tabs
            value={tabValue}
            onChange={(e, value) => setTabValue(value)}
            indicatorColor="secondary"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                className={tabValue === tab.value ? "gradient-text" : ""}
                label={tab.name}
                value={tab.value}
                disabled={!isUserMatched}
              />
            ))}
          </Tabs>
        </Stack>
      </Box>
      {tabValue === "details" && (
        <Stack direction="column" spacing={2} sx={{ padding: "3%" }}>
          <Typography variant="h3" color={"var(--main-text-color)"}>
            Details
          </Typography>
          <Stack className="box" direction="column" spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconFileDescription size="20" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Description
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="bodysm" color="var(--body-text-color-600)">
              {campaign.description}
            </Typography>
            <Divider />
            <Stack direction="row" width="100%" spacing={2}>
              <Stack direction="row" spacing={2} width="25%">
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconApps
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Platforms
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    TikTok, Instagram
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" width="25%" spacing={2}>
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconUsersGroup
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Creator Levels
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    Micro, Macro
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" width="25%" spacing={2}>
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconBuilding
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Industry
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    Travel, Beauty
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" width="25%" spacing={2}>
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconMapPin
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Region
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    USA
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack className="box" direction="column" spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                width="100%"
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    className="gradient-circle"
                    sx={{
                      padding: "8px",
                      alignItems: "center",
                      justifyItems: "center",
                    }}
                  >
                    <IconListCheck size="20" className="icon" />
                  </Box>
                  <Typography variant="h4" color={"var(--main-text-color)"}>
                    Deliverables
                  </Typography>
                </Stack>
                {/* Add progress bar once deliverables are changed to a list format with specific fields */}
                {/* <Stack sx={{ width: '15%'}}>
                                    <LinearProgress variant="determinate" value="50" color="success" sx={{borderRadius: 5, height: "6px"}}/>
                                </Stack>
                                <Typography variant="bodyxs" color="var(--body-text-color-600)">1/3 completed</Typography> */}
              </Stack>
              {/* <Button className="outlined-button" variant="outlined" startIcon={<IconEdit className="icon-colored" size="18" color={theme.palette.primary.main}/>}>Edit Deliverables</Button> */}
            </Stack>
            <Divider />
            <Typography variant="bodysm" color="var(--body-text-color-600)" style={{whiteSpace: "pre-wrap"}}>
              {Array.isArray(campaign.deliverables) ? campaign.deliverables.join("\n") : campaign.deliverables}
            </Typography>
            {/* Create table once deliverables are changed to a list format with specific fields */}
            {/* <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer> */}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Stack className="box" direction="column" spacing={2} width="50%">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconPresentation size="20" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Talking Points
                </Typography>
              </Stack>
              <Divider />
              {campaign.talking_points &&
                campaign.talking_points
                  .split("\n")
                  .filter((point) => point.trim().length > 0)
                  .map((point, index) => (
                  <Stack key={index} direction="row" gap={3}>
                    <Box sx={{ width: "30px" }}>
                      <IconMicrophone2
                        className="icon-colored"
                        size="25"
                        color={theme.palette.secondary.light}
                      />
                    </Box>
                    <Typography
                      variant="bodysm"
                      color="var(--body-text-color-600)"
                    >
                      {point.replace(/^- /, "")}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
            <Stack className="box" direction="column" spacing={2} width="50%">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconInfoCircle size="28" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Resources
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="column" spacing={2}>
                <Link href="/campaigns/faq" target="_blank" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>

                  <Typography variant="bodysm" color="var(--body-text-color-600)">
                    Tutorials & FAQ
                  </Typography>
                  <IconLink size="20" className="icon-colored"  color="var(--main-text-color)"/>
                </Link>
<Stack direction="row" alignItems="center" spacing={2} style={{cursor: "pointer"}}>
<Typography variant="bodysm" color="var(--body-text-color-600)">
You're a rockstar! Congrats on launching your campaign. Here are some tips to get your started

                  </Typography>
                  <IconInfoCircle size="20" className="icon-colored"  color="var(--main-text-color)"/>
                  </Stack>
              </Stack>
              {/* Add links to resources here */}
            </Stack>
          </Stack>
        </Stack>
      )}
      {tabValue === "workstream" && <CreatorWorkstreamTab campaign={campaign} />}
      {tabValue === "analytics" && isUserMatched && <CreatorAnalytics />}
      {tabValue === "live" && <CreatorLiveContent />}
    </Box>
  );
}
